import { css, StyleSheet } from 'aphrodite';
import { FC, useEffect, useRef, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';

import type { CallState } from '../call';
import { IconGrid, IconMic, IconMicOff, IconPhone, IconPhoneOff, IconX } from './icons';

/**
 * Props for {@link CallStrip}. See `ui/README.md` for the state matrix and
 * `call/README.md` for the underlying `CallState` discriminated union.
 */
export type CallStripProps = {
	callState: CallState;
	muted: boolean;
	/** Accept the currently-ringing call. No-op when `callState.status !== 'ringing'`. */
	onAnswer: () => void;
	/** Reject/ignore the currently-ringing call. */
	onReject: () => void;
	/** End the active or dialing call. */
	onHangup: () => void;
	/** Toggle mute on the active call. */
	onToggleMute: () => void;
	/**
	 * Initialize the Twilio Device. Browsers block AudioContext until a user
	 * gesture, so this is wired to a button rather than fired on mount.
	 */
	onEnableAudio: () => void;
	/** Preferred display name, e.g. "Jes Lipson · +1 (919) 555-0142". */
	callerDisplay?: string;

	/**
	 * Display string for the currently-ringing inbound caller, resolved from
	 * the phone-call record + lead contacts. Used by the `ringing` branch in
	 * preference to `callerDisplay` (which would be the LEAD currently
	 * loaded in the focused view — that lead may be a different person
	 * than the one calling in).
	 */
	inboundDisplay?: string;
	/**
	 * Click handler for the ringing-strip's caller display. When provided
	 * and a resolved `inboundDisplay` is rendered, the name becomes a
	 * clickable link so the rep can preview the caller's lead before
	 * deciding whether to answer.
	 */
	onOpenInboundLead?: () => void;
	/**
	 * Send a DTMF digit on the active call. Only invoked while the strip is
	 * in the `connected` state (the dialpad is hidden otherwise). Optional
	 * so the harness page and tests can render the strip without a pipeline.
	 */
	onSendDigit?: (digit: string) => void;
	/**
	 * True while the Twilio SDK is mid-recovery from a transient audio drop on
	 * the active call. When set, the connected strip swaps the LIVE pill for
	 * an amber "Reconnecting…" pill so reps notice silent dropouts. Cleared on
	 * `call:reconnected` or `call:ended` by the parent.
	 */
	reconnecting?: boolean;
};

/**
 * The top strip of the center column. Renders one of three visual modes based
 * on `callState.status`:
 *
 *  - idle/uninit/ended → a muted "Listening for incoming calls" bar with a
 *    pulsing green dot.
 *  - ringing → dark call strip with INCOMING CALL eyebrow, caller display,
 *    Answer button, and a small X reject button.
 *  - dialing → dark call strip with CALLING… eyebrow, peer display, and a
 *    Hangup button.
 *  - connected → dark call strip with a LIVE · MM:SS pill (elapsed time
 *    ticked locally from `callState.call.startedAt`), peer display, mute
 *    toggle, and a red End button.
 */
export const CallStrip: FC<CallStripProps> = ({
	callState,
	muted,
	onAnswer,
	onReject,
	onHangup,
	onToggleMute,
	onEnableAudio,
	callerDisplay,
	inboundDisplay,
	onOpenInboundLead,
	onSendDigit,
	reconnecting = false,
}) => {
	const status = callState.status;
	const [dialpadOpen, setDialpadOpen] = useState(false);

	// Auto-close the dialpad when the call ends so it doesn't linger over the
	// next idle/ringing strip (the connected layout is the only place the
	// dialpad is rendered, but the popover lives outside the strip and would
	// otherwise stay mounted across status transitions in dev builds).
	useEffect(() => {
		if (status !== 'connected' && dialpadOpen) setDialpadOpen(false);
	}, [status, dialpadOpen]);

	if (status === 'uninit') {
		return (
			<div className={css(styles.stripIdle)}>
				<span className={css(styles.eyebrowMuted)}>Call audio not enabled yet</span>
				<Button variant='primary' size='sm' onClick={onEnableAudio}>
					<IconPhone size={13} />
					Enable call audio
				</Button>
			</div>
		);
	}

	if (status === 'ringing') {
		// Inbound ringing precedence:
		//   1. `inboundDisplay` — server-resolved company + contact name from
		//      the phone-call record (see FocusedLeadView ringingInboundDisplay).
		//   2. `callState.incoming.fromDisplay` — anything the call payload itself
		//      shipped (currently unused, kept for symmetry).
		//   3. `callState.incoming.from` — raw E.164 fallback.
		//   4. `callerDisplay` — last-resort lead-of-focus fallback. May be the
		//      wrong person but better than nothing if everything else is empty.
		const display = inboundDisplay ?? callState.incoming.fromDisplay ?? callState.incoming.from ?? callerDisplay;
		// Make the caller display a link to the resolved lead only when we
		// actually resolved one (so a raw-number ringing strip stays plain
		// text — there's nothing to navigate to).
		const peerNameLinkable = Boolean(onOpenInboundLead && inboundDisplay);
		return (
			<div className={css(styles.stripDark)}>
				<span className={css(styles.dotWrapLarge)}>
					<span className={css(styles.dotCallInk, styles.pulseFast)} />
				</span>
				<div className={css(styles.peerWrap)}>
					<div className={css(styles.eyebrow)}>Incoming call</div>
					{peerNameLinkable ? (
						<button
							type='button'
							onClick={onOpenInboundLead}
							className={css(styles.peerName, styles.peerNameLink)}
							title='Open this lead before answering'
						>
							{display}
						</button>
					) : (
						<div className={css(styles.peerName)}>{display}</div>
					)}
				</div>
				<Button variant='success' size='sm' onClick={onAnswer}>
					<IconPhone size={13} />
					Answer
				</Button>
				<Button variant='ghost' size='icon' styles={[styles.callIconBtn]} onClick={onReject} aria-label='Reject call'>
					<IconX size={16} />
				</Button>
			</div>
		);
	}

	if (status === 'dialing') {
		const display = callerDisplay ?? callState.target.to;
		return (
			<div className={css(styles.stripDark)}>
				<span className={css(styles.dotWrapLarge)}>
					<span className={css(styles.dotCallInk, styles.pulseFast)} />
				</span>
				<div className={css(styles.peerWrap)}>
					<div className={css(styles.eyebrow)}>Calling…</div>
					<div className={css(styles.peerName)}>{display}</div>
				</div>
				<Button variant='destructive' size='sm' onClick={onHangup}>
					<IconPhoneOff size={13} />
					Hang up
				</Button>
			</div>
		);
	}

	if (status === 'connected') {
		const display = callerDisplay ?? callState.call.peer;
		return (
			<div className={css(styles.stripDark)}>
				{reconnecting ? <ReconnectingPill /> : <LivePill startedAt={callState.call.startedAt} />}
				<div className={css(styles.peerInline)}>{display}</div>
				{onSendDigit && (
					<DialpadToggle open={dialpadOpen} onOpenChange={setDialpadOpen} onSendDigit={digit => onSendDigit(digit)} />
				)}
				<Button
					variant='ghost'
					size='icon'
					styles={muted ? [styles.callIconBtn, styles.callMuteActiveBtn] : [styles.callIconBtn]}
					onClick={onToggleMute}
					title={muted ? 'Unmute' : 'Mute'}
					aria-pressed={muted}
				>
					{muted ? (
						<span className={css(styles.muteIconActive)}>
							<IconMicOff size={15} />
						</span>
					) : (
						<IconMic size={15} />
					)}
				</Button>
				<Button variant='destructive' size='sm' onClick={onHangup}>
					<IconPhoneOff size={13} />
					End
				</Button>
			</div>
		);
	}

	// 'idle' | 'ended' — listening-for-calls strip (device is initialized).
	return (
		<div className={css(styles.stripIdle)}>
			<span className={css(styles.dotWrapSmall)}>
				<span className={css(styles.dotGreen, styles.pulseSlow)} />
			</span>
			<span>Listening for incoming calls</span>
		</div>
	);
};

// 3x4 DTMF keypad — rows (1-2-3, 4-5-6, 7-8-9, *-0-#). Sent verbatim
// to `Call.sendDigits`; the Twilio SDK accepts each character one at a
// time and emits the corresponding tone on the active call.
const DIALPAD_ROWS: ReadonlyArray<ReadonlyArray<string>> = [
	['1', '2', '3'],
	['4', '5', '6'],
	['7', '8', '9'],
	['*', '0', '#'],
];

/**
 * Toggleable in-call dialpad anchored to the CallStrip. Opens an inline
 * popover (positioned absolute below the strip) so reps can navigate
 * phone-tree IVRs ("press 1 for sales…") without leaving the focused view.
 *
 * Closes on outside click and on Escape; the strip's internal state ensures
 * a transition out of the `connected` status also closes it (handled by the
 * parent `CallStrip` effect).
 */
const DialpadToggle: FC<{
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSendDigit: (digit: string) => void;
}> = ({ open, onOpenChange, onSendDigit }) => {
	const popoverRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!open) return;
		const handleClick = (e: MouseEvent) => {
			const target = e.target as Node | null;
			if (!target) return;
			if (popoverRef.current?.contains(target)) return;
			if (buttonRef.current?.contains(target)) return;
			onOpenChange(false);
		};
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onOpenChange(false);
		};
		document.addEventListener('mousedown', handleClick);
		document.addEventListener('keydown', handleKey);
		return () => {
			document.removeEventListener('mousedown', handleClick);
			document.removeEventListener('keydown', handleKey);
		};
	}, [open, onOpenChange]);

	return (
		<div className={css(styles.dialpadAnchor)}>
			<Button
				ref={buttonRef}
				variant='ghost'
				size='icon'
				styles={open ? [styles.callIconBtn, styles.callIconBtnActive] : [styles.callIconBtn]}
				title={open ? 'Hide keypad' : 'Show keypad'}
				aria-label={open ? 'Hide DTMF keypad' : 'Show DTMF keypad'}
				aria-expanded={open}
				onClick={() => onOpenChange(!open)}
			>
				<IconGrid size={15} />
			</Button>
			{open && (
				<div ref={popoverRef} role='dialog' aria-label='DTMF keypad' className={css(styles.dialpadPopover)}>
					<div className={css(styles.dialpadGrid)}>
						{DIALPAD_ROWS.map(row =>
							row.map(digit => (
								<button
									key={digit}
									type='button'
									className={css(styles.dialpadKey)}
									onClick={() => onSendDigit(digit)}
									aria-label={`Send DTMF tone ${digit}`}
								>
									{digit}
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * Renders an amber "Reconnecting…" pill that swaps in for the LIVE pill while
 * Twilio is recovering signaling on an active call. Lower-saturation than the
 * destructive red treatment used for End — reconnects often resolve, so the
 * indicator is informational, not alarming.
 */
const ReconnectingPill: FC = () => (
	<div className={css(styles.reconnectPill)}>
		<span className={css(styles.dotWrapSmall)}>
			<span className={css(styles.dotAmber, styles.pulseFast)} />
		</span>
		<span className={css(styles.reconnectText)}>Reconnecting…</span>
	</div>
);

/**
 * Renders the "LIVE · MM:SS" pill for an active call. Ticks once per second
 * while mounted; cleans the interval up on unmount.
 */
const LivePill: FC<{ startedAt: number }> = ({ startedAt }) => {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const id = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(id);
	}, []);

	const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
	const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
	const ss = String(elapsed % 60).padStart(2, '0');

	return (
		<div className={css(styles.livePill)}>
			<span className={css(styles.dotWrapSmall)}>
				<span className={css(styles.dotCallInk, styles.pulseSlow)} />
			</span>
			<span className={css(styles.liveText)}>{`LIVE · ${mm}:${ss}`}</span>
		</div>
	);
};

const pulseKeyframes = {
	'0%': { opacity: 1, transform: 'scale(1)' },
	'50%': { opacity: 0.5, transform: 'scale(1.3)' },
	'100%': { opacity: 1, transform: 'scale(1)' },
};

// Subtle pulse on the mute-active mic-off icon. Lower-amplitude than
// `pulseKeyframes` (no scale change) so the destructive red treatment reads as
// "actively muted" without becoming a distracting heartbeat.
const muteActivePulseKeyframes = {
	'0%': { opacity: 1 },
	'50%': { opacity: 0.55 },
	'100%': { opacity: 1 },
};

const styles = StyleSheet.create({
	stripIdle: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 10,
		padding: '10px 20px',
		borderBottom: '1px solid var(--border)',
		color: 'var(--ink-muted)',
		fontSize: 12,
		flexShrink: 0,
	},
	eyebrowMuted: {
		fontSize: 12,
		color: 'var(--ink-muted)',
	},
	stripDark: {
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		padding: '12px 16px 12px 20px',
		borderBottom: '1px solid var(--border)',
		background: 'var(--call-bg)',
		color: 'var(--call-ink)',
		flexShrink: 0,
	},
	dotWrapSmall: {
		position: 'relative',
		width: 8,
		height: 8,
		display: 'inline-block',
		flexShrink: 0,
	},
	dotWrapLarge: {
		position: 'relative',
		width: 10,
		height: 10,
		display: 'inline-block',
		flexShrink: 0,
	},
	dotGreen: {
		position: 'absolute',
		inset: 0,
		background: '#10A37F',
		borderRadius: 999,
	},
	dotCallInk: {
		position: 'absolute',
		inset: 0,
		background: 'var(--call-ink)',
		borderRadius: 999,
	},
	dotAmber: {
		position: 'absolute',
		inset: 0,
		background: '#F59E0B',
		borderRadius: 999,
	},
	pulseSlow: {
		animationName: [pulseKeyframes],
		animationDuration: '1.6s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'ease-in-out',
	},
	pulseFast: {
		animationName: [pulseKeyframes],
		animationDuration: '1s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'ease-in-out',
	},
	peerWrap: {
		flex: 1,
		minWidth: 0,
	},
	eyebrow: {
		fontSize: 11,
		opacity: 0.7,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
	},
	peerName: {
		fontSize: 14,
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	peerNameLink: {
		background: 'transparent',
		border: 'none',
		padding: 0,
		margin: 0,
		color: 'inherit',
		font: 'inherit',
		fontWeight: 500,
		cursor: 'pointer',
		textAlign: 'left',
		textDecoration: 'underline',
		textDecorationColor: 'rgba(255, 255, 255, 0.45)',
		textUnderlineOffset: 2,
		':hover': {
			textDecorationColor: 'currentColor',
		},
		':focus-visible': {
			outline: '2px solid rgba(255, 255, 255, 0.5)',
			outlineOffset: 2,
		},
	},
	peerInline: {
		flex: 1,
		minWidth: 0,
		fontSize: 13,
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	// Square icon control on the dark CallStrip. Layered onto the components-v2
	// Button `ghost`/`icon` base via the `styles` prop: we keep the Button's
	// focus-visible, disabled, and transition handling but override fill + hover
	// for the dark surface (ghost's near-white hover would wash out the light
	// icon). Used by the mute, dialpad-toggle, and reject controls.
	callIconBtn: {
		width: 32,
		height: 32,
		padding: 0,
		borderRadius: 8,
		background: 'rgba(255, 255, 255, 0.08)',
		color: 'var(--call-ink)',
		':hover': {
			background: 'rgba(255, 255, 255, 0.16)',
		},
	},
	// "Pressed" treatment for toggle controls (dialpad open) — brighter than the
	// resting fill so the active state is legible against the strip.
	callIconBtnActive: {
		background: 'rgba(255, 255, 255, 0.18)',
		':hover': {
			background: 'rgba(255, 255, 255, 0.24)',
		},
	},
	// Destructive/active mute treatment: red tint so reps notice the muted state
	// at a glance instead of mistaking it for the inactive button.
	callMuteActiveBtn: {
		background: 'rgba(239, 68, 68, 0.2)',
		color: '#FCA5A5',
		':hover': {
			background: 'rgba(239, 68, 68, 0.28)',
		},
	},
	livePill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		padding: '3px 10px 3px 6px',
		background: 'rgba(255, 255, 255, 0.12)',
		borderRadius: 999,
		fontSize: 12,
		flexShrink: 0,
	},
	liveText: {
		fontFamily: 'var(--font-mono)',
		fontSize: 11,
	},
	reconnectPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		padding: '3px 10px 3px 6px',
		background: 'rgba(245, 158, 11, 0.18)',
		borderRadius: 999,
		fontSize: 12,
		flexShrink: 0,
		color: '#FCD34D',
	},
	reconnectText: {
		fontSize: 11,
		letterSpacing: '0.02em',
	},
	muteIconActive: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: '#FCA5A5',
		animationName: [muteActivePulseKeyframes],
		animationDuration: '1.6s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'ease-in-out',
	},
	dialpadAnchor: {
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
	},
	dialpadPopover: {
		position: 'absolute',
		top: 'calc(100% + 8px)',
		right: 0,
		zIndex: 60,
		padding: 10,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
		borderRadius: 12,
		boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.18))',
	},
	dialpadGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 44px)',
		gridTemplateRows: 'repeat(4, 44px)',
		gap: 6,
	},
	dialpadKey: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 44,
		width: 44,
		borderRadius: 8,
		background: 'transparent',
		border: '1px solid var(--border)',
		color: 'var(--ink)',
		fontSize: 16,
		fontWeight: 500,
		fontFamily: 'var(--font-mono)',
		cursor: 'pointer',
		':hover': {
			background: 'var(--hover, rgba(0, 0, 0, 0.04))',
		},
		':active': {
			background: 'var(--bg-elev)',
		},
	},
});
